import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Landmark,
  RefreshCw,
  Download,
  ArrowDownLeft,
  ArrowUpRight,
  AlertCircle,
  CheckCircle2,
  FileText,
  Receipt,
  Plus,
  FileDown,
} from 'lucide-react';
import { format, subDays, addDays, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  interBankService,
  InterBankStatus,
  InterExtrato,
  InterSaldo,
  InterTransacao,
  InterCobrancasResponse,
  EmitirCobrancaPayload,
} from '@/services/interBankService';
import { useInterBankSyncPoller } from '@/hooks/useInterBankSyncPoller';

function formatCurrency(value?: string | number) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === undefined || Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
}

function formatTransacaoDetalhes(tx: InterTransacao): string | null {
  if (tx.descricao) return tx.descricao;
  if (!tx.detalhes) return null;
  if (typeof tx.detalhes === 'string') return tx.detalhes;
  const partes = [
    tx.detalhes.nomeRecebedor || tx.detalhes.nomePagador,
    tx.detalhes.descricaoPix,
    tx.detalhes.endToEndId,
  ].filter(Boolean);
  return partes.length > 0 ? partes.join(' · ') : null;
}

function defaultDateRange() {
  const fim = new Date();
  const inicio = subDays(fim, 89); // 90 dias inclusivos (limite da API Inter)
  return {
    dataInicio: format(inicio, 'yyyy-MM-dd'),
    dataFim: format(fim, 'yyyy-MM-dd'),
  };
}

const formBoletoVazio = (): EmitirCobrancaPayload => ({
  seuNumero: '',
  valorNominal: 0,
  dataVencimento: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
  numDiasAgenda: 30,
  pagador: {
    nome: '',
    cpfCnpj: '',
    endereco: '',
    cidade: '',
    uf: '',
    cep: '',
  },
});

export function BancoInter() {
  const [status, setStatus] = useState<InterBankStatus | null>(null);
  const [saldo, setSaldo] = useState<InterSaldo | null>(null);
  const [extrato, setExtrato] = useState<InterExtrato | null>(null);
  const [cobrancas, setCobrancas] = useState<InterCobrancasResponse | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingExtrato, setLoadingExtrato] = useState(false);
  const [loadingCobrancas, setLoadingCobrancas] = useState(false);
  const [abaInterna, setAbaInterna] = useState('extrato');
  const [datas, setDatas] = useState(defaultDateRange);
  const [tipoOperacao, setTipoOperacao] = useState<string>('todos');
  const [dialogEmitir, setDialogEmitir] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [formBoleto, setFormBoleto] = useState<EmitirCobrancaPayload>(formBoletoVazio);
  const [baixandoPdf, setBaixandoPdf] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [lastNotificationAt, setLastNotificationAt] = useState<string | null>(null);
  const [registrandoWebhooks, setRegistrandoWebhooks] = useState(false);
  const { syncStatus, syncVersion, refreshCounter } = useInterBankSyncPoller({
    enabled: !!status?.connected,
  });

  useEffect(() => {
    if (!syncStatus) return;
    setLastSyncAt(syncStatus.lastSyncAt || null);
    setLastNotificationAt(syncStatus.lastNotificationAt || null);
    if (syncStatus.saldo) setSaldo(syncStatus.saldo);
  }, [syncStatus]);

  const carregarStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const data = await interBankService.getStatus();
      setStatus(data);
      if (data.connected && data.saldo) {
        setSaldo(data.saldo);
      } else if (data.connected) {
        const saldoData = await interBankService.getSaldo();
        setSaldo(saldoData);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao verificar integração com o Banco Inter');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    carregarStatus();
  }, [carregarStatus]);

  const carregarExtrato = useCallback(async () => {
    if (!status?.connected) return;
    try {
      setLoadingExtrato(true);
      const params = {
        ...datas,
        tamanhoPagina: 100,
        ...(tipoOperacao !== 'todos' ? { tipoOperacao } : {}),
      };
      const data = await interBankService.getExtratoCompleto(params);
      setExtrato(data);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar extrato');
    } finally {
      setLoadingExtrato(false);
    }
  }, [status?.connected, datas, tipoOperacao]);

  const carregarCobrancas = useCallback(async () => {
    if (!status?.connected) return;
    try {
      setLoadingCobrancas(true);
      const data = await interBankService.listarCobrancas({
        dataInicial: datas.dataInicio,
        dataFinal: datas.dataFim,
        tamanhoPagina: 50,
      });
      setCobrancas(data);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar cobranças');
    } finally {
      setLoadingCobrancas(false);
    }
  }, [status?.connected, datas.dataInicio, datas.dataFim]);

  useEffect(() => {
    if (status?.connected && abaInterna === 'extrato') {
      carregarExtrato();
    }
    if (status?.connected && abaInterna === 'cobrancas') {
      carregarCobrancas();
    }
  }, [status?.connected, abaInterna, carregarExtrato, carregarCobrancas]);

  // Nova sincronização: recarrega o extrato no período selecionado (não usa o cache curto do sync)
  useEffect(() => {
    if (refreshCounter === 0) return;
    if (status?.connected && abaInterna === 'extrato') {
      carregarExtrato();
    }
  }, [refreshCounter, status?.connected, abaInterna, carregarExtrato]);

  const exportarPdf = async () => {
    try {
      toast.info('Gerando PDF do extrato...');
      const blob = await interBankService.exportarExtratoPdf(datas.dataInicio, datas.dataFim);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `extrato-inter-${datas.dataInicio}_${datas.dataFim}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Extrato exportado');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao exportar extrato');
    }
  };

  const baixarPdfBoleto = async (codigoSolicitacao: string, seuNumero?: string) => {
    try {
      setBaixandoPdf(codigoSolicitacao);
      const blob = await interBankService.baixarCobrancaPdf(codigoSolicitacao);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `boleto-${seuNumero || codigoSolicitacao}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF do boleto baixado');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao baixar PDF do boleto');
    } finally {
      setBaixandoPdf(null);
    }
  };

  const emitirBoleto = async () => {
    if (!formBoleto.seuNumero || !formBoleto.pagador.nome || !formBoleto.pagador.cpfCnpj || !formBoleto.valorNominal) {
      toast.error('Preencha seu número, valor, nome e CPF/CNPJ do pagador');
      return;
    }
    try {
      setEmitindo(true);
      const result = await interBankService.emitirCobranca(formBoleto);
      toast.success('Boleto emitido com sucesso');
      setDialogEmitir(false);
      setFormBoleto(formBoletoVazio());
      if (abaInterna === 'cobrancas') {
        await carregarCobrancas();
      }
      if (result.codigoSolicitacao) {
        toast.info('Aguarde alguns segundos e baixe o PDF quando a cobrança estiver processada');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao emitir boleto');
    } finally {
      setEmitindo(false);
    }
  };

  const registrarWebhooks = async () => {
    try {
      setRegistrandoWebhooks(true);
      await interBankService.registrarWebhooks();
      toast.success('Webhooks registrados no Banco Inter');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao registrar webhooks');
    } finally {
      setRegistrandoWebhooks(false);
    }
  };

  const transacoes: InterTransacao[] = extrato?.transacoes || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Landmark className="h-5 w-5 text-primary" />
            Banco Inter
          </h2>
          <p className="text-sm text-muted-foreground">
            Consulta de saldo, extrato e cobranças via API Inter Empresas
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={carregarStatus} disabled={loadingStatus} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${loadingStatus ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Status da integração</CardTitle>
          <CardDescription>
            {status?.sandbox ? 'Ambiente Sandbox' : 'Ambiente de produção'}
            {status?.contaCorrente ? ` · Conta ${status.contaCorrente}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingStatus ? (
            <div className="text-sm text-muted-foreground">Verificando conexão...</div>
          ) : !status?.configured ? (
            <div className="flex items-start gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Integração não configurada</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure as variáveis INTER_* no backend. Consulte docs/INTEGRACAO_BANCO_INTER.md
                </p>
              </div>
            </div>
          ) : status.connected ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Conectado à API do Inter</span>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                {lastSyncAt && (
                  <p>
                    Última sincronização:{' '}
                    {formatDistanceToNow(new Date(lastSyncAt), { addSuffix: true, locale: ptBR })}
                    {syncVersion > 0 ? ` (v${syncVersion})` : ''}
                  </p>
                )}
                {lastNotificationAt && (
                  <p>
                    Última notificação:{' '}
                    {formatDistanceToNow(new Date(lastNotificationAt), { addSuffix: true, locale: ptBR })}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={registrandoWebhooks}
                  onClick={registrarWebhooks}
                >
                  {registrandoWebhooks ? 'Registrando...' : 'Registrar webhooks'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-destructive">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Falha na conexão</p>
                <p className="text-sm mt-1">{status.error}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {status?.connected && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Saldo disponível</CardDescription>
              <CardTitle className="text-2xl">{formatCurrency(saldo?.disponivel)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Limite</CardDescription>
              <CardTitle className="text-2xl">{formatCurrency(saldo?.limite)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Bloqueado</CardDescription>
              <CardTitle className="text-2xl">
                {formatCurrency(
                  (Number(saldo?.bloqueadoCheque) || 0) +
                    (Number(saldo?.bloqueadoJudicialmente) || 0) +
                    (Number(saldo?.bloqueadoAdministrativo) || 0)
                )}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {status?.connected && (
        <Card>
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <CardTitle className="text-base">Movimentações</CardTitle>
                <CardDescription>Período máximo de 90 dias por consulta</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="date"
                  value={datas.dataInicio}
                  onChange={(e) => setDatas((d) => ({ ...d, dataInicio: e.target.value }))}
                  className="w-full sm:w-auto"
                />
                <Input
                  type="date"
                  value={datas.dataFim}
                  onChange={(e) => setDatas((d) => ({ ...d, dataFim: e.target.value }))}
                  className="w-full sm:w-auto"
                />
                <Select value={tipoOperacao} onValueChange={setTipoOperacao}>
                  <SelectTrigger className="w-full sm:w-[140px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="C">Créditos</SelectItem>
                    <SelectItem value="D">Débitos</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={abaInterna === 'extrato' ? carregarExtrato : carregarCobrancas} disabled={loadingExtrato || loadingCobrancas}>
                  Buscar
                </Button>
                {abaInterna === 'extrato' && (
                  <Button variant="outline" onClick={exportarPdf} className="gap-2">
                    <Download className="h-4 w-4" />
                    PDF
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={abaInterna} onValueChange={setAbaInterna}>
              <TabsList>
                <TabsTrigger value="extrato" className="gap-2">
                  <FileText className="h-4 w-4" />
                  Extrato
                </TabsTrigger>
                <TabsTrigger value="cobrancas" className="gap-2">
                  <Receipt className="h-4 w-4" />
                  Cobranças
                </TabsTrigger>
              </TabsList>

              <TabsContent value="extrato" className="mt-4">
                {loadingExtrato ? (
                  <div className="py-8 text-center text-muted-foreground">Carregando extrato...</div>
                ) : transacoes.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">Nenhuma transação no período</div>
                ) : (
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transacoes.map((tx, idx) => {
                          const detalheTexto = formatTransacaoDetalhes(tx);
                          return (
                          <TableRow key={`${tx.dataEntrada}-${idx}`}>
                            <TableCell className="whitespace-nowrap">
                              {tx.dataEntrada || tx.dataTransacao || '—'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={tx.tipoOperacao === 'C' ? 'default' : 'secondary'} className="gap-1">
                                {tx.tipoOperacao === 'C' ? (
                                  <ArrowDownLeft className="h-3 w-3" />
                                ) : (
                                  <ArrowUpRight className="h-3 w-3" />
                                )}
                                {tx.tipoTransacao || (tx.tipoOperacao === 'C' ? 'Crédito' : 'Débito')}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-md">
                              <div className="font-medium">{tx.titulo || '—'}</div>
                              {detalheTexto && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {detalheTexto}
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              className={`text-right font-medium whitespace-nowrap ${
                                tx.tipoOperacao === 'C' ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {tx.tipoOperacao === 'D' ? '-' : '+'}
                              {formatCurrency(tx.valor)}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="cobrancas" className="mt-4">
                <div className="flex justify-end mb-3">
                  <Button onClick={() => setDialogEmitir(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Emitir boleto
                  </Button>
                </div>
                {loadingCobrancas ? (
                  <div className="py-8 text-center text-muted-foreground">Carregando cobranças...</div>
                ) : !cobrancas?.cobrancas?.length ? (
                  <div className="py-8 text-center text-muted-foreground">Nenhuma cobrança no período</div>
                ) : (
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Seu número</TableHead>
                          <TableHead>Pagador</TableHead>
                          <TableHead>Vencimento</TableHead>
                          <TableHead>Situação</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cobrancas.cobrancas.map((c) => (
                          <TableRow key={c.codigoSolicitacao || c.seuNumero}>
                            <TableCell>{c.seuNumero || c.codigoSolicitacao || '—'}</TableCell>
                            <TableCell>
                              <div>{c.pagador?.nome || '—'}</div>
                              {c.pagador?.cpfCnpj && (
                                <div className="text-xs text-muted-foreground">{c.pagador.cpfCnpj}</div>
                              )}
                            </TableCell>
                            <TableCell>{c.dataVencimento || '—'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{c.situacao || '—'}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(c.valorNominal)}
                            </TableCell>
                            <TableCell className="text-right">
                              {c.codigoSolicitacao && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1"
                                  disabled={baixandoPdf === c.codigoSolicitacao}
                                  onClick={() => baixarPdfBoleto(c.codigoSolicitacao!, c.seuNumero)}
                                >
                                  <FileDown className="h-3 w-3" />
                                  PDF
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogEmitir} onOpenChange={setDialogEmitir}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Emitir boleto</DialogTitle>
            <DialogDescription>
              Emissão via API Inter (boleto com Pix). A cobrança é processada de forma assíncrona.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="seuNumero">Seu número *</Label>
                <Input
                  id="seuNumero"
                  placeholder="Ex: CR-12345"
                  value={formBoleto.seuNumero}
                  onChange={(e) => setFormBoleto((f) => ({ ...f, seuNumero: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valorNominal">Valor (R$) *</Label>
                <Input
                  id="valorNominal"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formBoleto.valorNominal || ''}
                  onChange={(e) =>
                    setFormBoleto((f) => ({ ...f, valorNominal: parseFloat(e.target.value) || 0 }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="dataVencimento">Vencimento *</Label>
                <Input
                  id="dataVencimento"
                  type="date"
                  value={formBoleto.dataVencimento}
                  onChange={(e) => setFormBoleto((f) => ({ ...f, dataVencimento: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="numDiasAgenda">Dias após vencimento</Label>
                <Input
                  id="numDiasAgenda"
                  type="number"
                  min="0"
                  value={formBoleto.numDiasAgenda ?? 30}
                  onChange={(e) =>
                    setFormBoleto((f) => ({ ...f, numDiasAgenda: parseInt(e.target.value, 10) || 30 }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pagadorNome">Nome do pagador *</Label>
              <Input
                id="pagadorNome"
                value={formBoleto.pagador.nome}
                onChange={(e) =>
                  setFormBoleto((f) => ({ ...f, pagador: { ...f.pagador, nome: e.target.value } }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pagadorCpf">CPF/CNPJ *</Label>
              <Input
                id="pagadorCpf"
                value={formBoleto.pagador.cpfCnpj}
                onChange={(e) =>
                  setFormBoleto((f) => ({ ...f, pagador: { ...f.pagador, cpfCnpj: e.target.value } }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pagadorEndereco">Endereço</Label>
              <Input
                id="pagadorEndereco"
                value={formBoleto.pagador.endereco || ''}
                onChange={(e) =>
                  setFormBoleto((f) => ({ ...f, pagador: { ...f.pagador, endereco: e.target.value } }))
                }
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="pagadorCidade">Cidade</Label>
                <Input
                  id="pagadorCidade"
                  value={formBoleto.pagador.cidade || ''}
                  onChange={(e) =>
                    setFormBoleto((f) => ({ ...f, pagador: { ...f.pagador, cidade: e.target.value } }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pagadorUf">UF</Label>
                <Input
                  id="pagadorUf"
                  maxLength={2}
                  value={formBoleto.pagador.uf || ''}
                  onChange={(e) =>
                    setFormBoleto((f) => ({ ...f, pagador: { ...f.pagador, uf: e.target.value.toUpperCase() } }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pagadorCep">CEP</Label>
              <Input
                id="pagadorCep"
                value={formBoleto.pagador.cep || ''}
                onChange={(e) =>
                  setFormBoleto((f) => ({ ...f, pagador: { ...f.pagador, cep: e.target.value } }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogEmitir(false)}>
              Cancelar
            </Button>
            <Button onClick={emitirBoleto} disabled={emitindo} className="gap-2">
              {emitindo ? 'Emitindo...' : 'Emitir boleto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
